import type { oauth as en } from "../en/oauth";
import type { Messages } from "../types";

export const oauth: Messages<typeof en> = {
  explainer: {
    title: "ربط إنستغرام",
    intro: "ستنتقل إلى إنستغرام لاختيار الحساب والموافقة على هذه الصلاحيات. ملفك لا يرى كلمة المرور أبدًا.",
    permissionsTitle: "ما يطلبه ملفك ولماذا",
    permissions: {
      instagram_business_basic: { title: "الملف الشخصي والمنشورات", why: "اسم الحساب وصورته والمتابعون ومنشوراتك ومقاطع الريلز، لتظهر داخل منصة ملفك." },
      instagram_business_manage_comments: { title: "التعليقات", why: "قراءة التعليقات على منشوراتك والرد عليها وإخفاؤها وحذفها من صندوق الوارد والأتمتة." },
      instagram_business_manage_messages: { title: "الرسائل المباشرة", why: "قراءة الرسائل والرد عليها. يسمح إنستغرام بالرد خلال 24 ساعة فقط من آخر رسالة للعميل." },
      instagram_business_content_publish: { title: "النشر", why: "نشر المنشورات والريلز والقصص التي تجدولها في منصة ملفك." },
      instagram_business_manage_insights: { title: "الإحصاءات", why: "الوصول والمشاهدات والتفاعل للوحات المعلومات والتقارير." },
    },
    importNote: "بعد الموافقة يستورد ملفك منشوراتك وتعليقاتك ورسائلك في الخلفية، الأحدث أولًا. يمكنك متابعة العمل أثناء ذلك.",
    neverPosts: "لا ينشر ملفك ولا يرد ولا يرسل باسمك إلا إذا طلبت ذلك أنت أو قاعدة أنشأتها.",
    accountsNote: "يتيح لك إنستغرام اختيار الحساب أو التبديل بينها، فيمكنك ربط عدة حسابات واحدًا تلو الآخر.",
    requirement: "يتطلب حساب إنستغرام تجاريًا أو حساب صانع محتوى.",
    continue: "المتابعة إلى إنستغرام",
    cancel: "ليس الآن",
    privacy: "سياسة الخصوصية",
  },
  returned: {
    connected: "تم ربط ‎@{{user}}",
    connectedGeneric: "تم ربط إنستغرام",
    connectedHint: "نستورد سجلّك في الخلفية. تظهر المنشورات والتعليقات الجديدة فور وصولها.",
    failedTitle: "لم يتم ربط إنستغرام",
    errors: {
      access_denied: "ألغيت العملية على إنستغرام، فلم يُربط شيء. يمكنك المحاولة مجددًا في أي وقت.",
      state_invalid: "رابط تسجيل الدخول غير صالح. ابدأ من جديد من منصة ملفك.",
      state_expired: "استغرق تسجيل الدخول وقتًا طويلًا وانتهت صلاحيته. ابدأ من جديد.",
      state_reused: "استُخدم رابط تسجيل الدخول هذا من قبل. ابدأ من جديد من منصة ملفك.",
      forbidden: "يستطيع مالكو المؤسسة ومشرفوها فقط ربط الحسابات. اطلب من مشرف ربطه.",
      exchange_failed: "لم يؤكد إنستغرام تسجيل الدخول. حاول مجددًا بعد دقيقة.",
      profile_failed: "تعذّرت قراءة ملف الحساب من إنستغرام. حاول مجددًا.",
      not_professional: "حساب إنستغرام هذا حساب شخصي. حوّله إلى حساب احترافي (تجاري أو صانع محتوى) من إعدادات إنستغرام ثم اربطه مجددًا.",
      not_tester: "حساب إنستغرام هذا ليس مختبِرًا للتطبيق بعد. أضفه من App roles ← Instagram testers في لوحة Meta واقبل الدعوة في إنستغرام (الإعدادات ← أذونات المواقع ← دعوات المختبِرين)، ثم حاول مجددًا.",
      account_conflict: "حساب إنستغرام هذا مربوط بمؤسسة أخرى. افصله هناك أولًا.",
      not_configured: "تسجيل الدخول عبر إنستغرام غير مُعدّ على هذا الخادم بعد. تواصل مع المسؤول.",
      server_error: "حدث خطأ من جهتنا. حاول مجددًا.",
      unknown: "لم يتم ربط إنستغرام ({{code}}). حاول مجددًا.",
    },
  },
  token: {
    label: "الوصول",
    expires: "يتجدد تلقائيًا · ينتهي {{when}}",
    soon: "ينتهي {{when}}: أعد الربط إن فشل التجديد",
    expired: "انتهى {{when}}: أعد الربط للمتابعة",
    unknown: "لا يوجد تاريخ انتهاء",
  },
  webhook: {
    since: "مشترك منذ {{when}}",
    failed: "فشل الاشتراك: {{error}}",
    fields: {
      comments: "التعليقات",
      live_comments: "تعليقات البث المباشر",
      mentions: "الإشارات",
      messages: "الرسائل المباشرة",
      message_edit: "الرسائل المعدّلة",
      message_reactions: "التفاعلات على الرسائل",
      messaging_postbacks: "الضغط على الأزرار في الرسائل",
      messaging_seen: "إشعارات القراءة",
      messaging_referral: "الإحالات من الإعلانات والروابط",
      messaging_optins: "موافقات تلقي الرسائل",
      messaging_handover: "تسليم المحادثات",
      story_insights: "إحصاءات القصص",
    },
    failedGeneric: "فشل الاشتراك. أعد الربط للمحاولة مجددًا.",
  },
  reauth: {
    title: "يلزم إعادة الربط",
    body: "توقف إنستغرام عن قبول وصول ملفك إلى ‎@{{user}} (انتهت صلاحيته أو أُزيل). أعد الربط لاستئناف التعليقات والرسائل والنشر.",
    needed: "يجب إعادة ربط إنستغرام",
    hint: "لم يعد إنستغرام يقبل وصول ملفك إلى هذا الحساب، فلم يُرسل شيء. أعد ربطه ثم حاول مجددًا.",
    hintUser: "لم يعد إنستغرام يقبل وصول ملفك إلى ‎@{{user}}، فلم يُرسل شيء. أعد ربطه ثم حاول مجددًا.",
    reconnect: "إعادة الربط",
  },
  backfill: {
    title: "استيراد ‎@{{user}}",
    titleGeneric: "استيراد السجل",
    hint: "الأحدث أولًا. كل ما استُورد حتى الآن موجود بالفعل في صندوق الوارد والمنشورات ولوحات المعلومات.",
    line: {
      posts: "المنشورات {{done}}",
      postsOf: "المنشورات {{done}}/{{total}}",
      comments: "التعليقات {{done}}",
      messages: "الرسائل {{done}}",
    },
    stage: {
      profile: "قراءة الملف الشخصي…",
      insights: "قراءة الإحصاءات…",
      media: "استيراد المنشورات والتعليقات…",
      messages: "استيراد الرسائل…",
      done: "اكتمل الاستيراد",
    },
    rateLimited_zero: "توقف مؤقتًا بسبب حد الطلبات في إنستغرام، {{count}} مرة",
    rateLimited_one: "توقف مؤقتًا بسبب حد الطلبات في إنستغرام، مرة واحدة",
    rateLimited_two: "توقف مؤقتًا بسبب حد الطلبات في إنستغرام، مرتين",
    rateLimited_few: "توقف مؤقتًا بسبب حد الطلبات في إنستغرام، {{count}} مرات",
    rateLimited_many: "توقف مؤقتًا بسبب حد الطلبات في إنستغرام، {{count}} مرة",
    rateLimited_other: "توقف مؤقتًا بسبب حد الطلبات في إنستغرام، {{count}} مرة",
    noAutomation: "السجل المستورد لا يشغّل الأتمتة ولا الردود التلقائية أبدًا.",
    missing: {
      short: "لم يُجلب {{n}} من التعليقات",
      title: "استيراد جزئي: لم يُجلب {{n}} من التعليقات",
      not_returned: "{{n}} لم يُرجعها إنستغرام. يحتسبها إنستغرام لكن واجهته البرمجية لا تعرضها (مثل تعليقات الحسابات المقيّدة أو المحجوزة للمراجعة).",
      api_error: "تعذّرت قراءة {{n}}: خطأ من واجهة إنستغرام{{detail}}.",
      recheck: "يعيد ملفك فحص هذه المنشورات تلقائيًا ويضيف أي تعليق يُرجعه إنستغرام لاحقًا.",
      posts: "المنشورات المتأثرة: {{n}}",
    },
  },
};
